import { AppleLogo } from '@/assets/apple-logo';
import { Button } from '@/components/ui/button';
import { env } from '@/env/client';
import { SignInWithApple } from '@capacitor-community/apple-sign-in';
import { Capacitor } from '@capacitor/core';
import { useAuthActions } from '@convex-dev/auth/react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { NATIVE_SOCIAL_LOGIN_REDIRECT_TO } from '../page';

export const AppleLogin = () => {
  const { t } = useTranslation('login');
  const { signIn } = useAuthActions();

  const platform = Capacitor.getPlatform();
  const isNative = Capacitor.isNativePlatform();

  const redirectTo = isNative
    ? `${NATIVE_SOCIAL_LOGIN_REDIRECT_TO}apple`
    : window.location.origin;

  const handleAppleLogin = async () => {
    try {
      // If on iOS, use native Apple Sign In
      if (platform === 'ios') {
        const { response } = await SignInWithApple.authorize({
          clientId: env.VITE_APPLE_CLIENT_ID,
          scopes: 'name email',
          redirectURI: '',
        });

        if (response.identityToken) {
          // 1. Create a strictly typed string dictionary
          const params: Record<string, string> = {
            identityToken: response.identityToken,
          };

          // 2. Only add optional fields if they exist (filtering out null/undefined)
          if (response.email) {
            params.email = response.email;
          }

          const fullName = [response.givenName, response.familyName]
            .filter(Boolean)
            .join(' ');

          if (fullName) {
            params.name = fullName;
          }

          // 3. Pass the clean object. No FormData, no nulls.
          await signIn('apple-native', params);
        }
      } else {
        // If on native platform, redirect to app using deep link
        // If on web, redirect back to current url
        const options = {
          redirectTo,
        };
        await signIn('apple', options);
      }
    } catch (error) {
      console.error('Apple Sign In failed:', error);
      toast.error(t('signInFailed'));
    }
  };

  return (
    <Button onClick={handleAppleLogin} variant="secondary" className="w-full">
      <div className="flex w-full flex-row items-center justify-center gap-2.5">
        <AppleLogo className="-mt-0.75 size-6" />
        <span className="text-sm">{t('socialLogin.apple')}</span>
      </div>
    </Button>
  );
};
